import type * as monacoTypes from 'monaco-editor';
import type { TemplateInputProps } from '../..';
import { findOpeningBraces, hasImmediateClosingBraces } from './braces-utils';

// One provider for the whole app session
let templateProviderRegistered = false;

// Per-model autocomplete data (weak keyed by ITextModel so GC can reclaim)
export const modelAutocomplete = new WeakMap<monacoTypes.editor.ITextModel, TemplateInputProps['autocompleteItems']>();

export const ensureTemplateProvider = (monaco: typeof import('monaco-editor')) => {
  if (templateProviderRegistered) return;

  const provider: monacoTypes.languages.CompletionItemProvider = {
    provideCompletionItems(model, position, context, _token) {
      const itemsForModel = modelAutocomplete.get(model) ?? {};
      const opening = findOpeningBraces(model, position);

      // Check if this is a manual trigger (Control + Space) without braces
      const isManualTrigger = context.triggerKind === monaco.languages.CompletionTriggerKind.Invoke;

      if (!opening) {
        // If no opening braces found and it's not a manual trigger, return no suggestions
        if (!isManualTrigger) return { suggestions: [] };

        // For manual trigger without braces, provide suggestions with full wrapping
        const word = model.getWordAtPosition(position);
        const replaceRange: monacoTypes.IRange = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word?.startColumn ?? position.column,
          endColumn: word?.endColumn ?? position.column,
        };

        const itemsArray = Object.values(itemsForModel).flat();
        const suggestions: monacoTypes.languages.CompletionItem[] = itemsArray.map(tv => ({
          label: tv.label,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: `{{ ${tv.value} }}`,
          range: replaceRange,
          sortText: `0_${tv.value}`,
          filterText: tv.value,
          detail: tv.detail,
          documentation: {
            value: `**${tv.label}**\n\n${tv.value ?? ''}`,
            isTrusted: true,
          },
        }));

        return { suggestions };
      }

      // When inside braces, always show all suggestions
      const word = model.getWordAtPosition(position);

      // Calculate the range to replace - from after {{ to end of word (or cursor if no word)
      const endColumn = word ? word.endColumn : position.column;

      // Check if there are closing braces
      const alreadyHasClosing = hasImmediateClosingBraces(model, position.lineNumber, endColumn);

      // If there are closing braces, extend the range to include spaces and the closing braces themselves
      let closingBracesColumn = endColumn;
      if (alreadyHasClosing) {
        const line = model.getLineContent(position.lineNumber);
        const afterWord = line.substring(endColumn - 1);
        const spacesAndClosing = afterWord.match(/^(\s*)\}\}/);
        if (spacesAndClosing) {
          // Extend to include spaces and the }} itself
          closingBracesColumn = endColumn + spacesAndClosing[0].length;
        }
      }

      const replaceRange: monacoTypes.IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: opening.startColumn,
        endColumn: alreadyHasClosing ? closingBracesColumn : endColumn,
      };

      const itemsArray = Object.values(itemsForModel).flat();

      // Always show all suggestions when inside braces, Monaco will handle filtering
      const suggestions: monacoTypes.languages.CompletionItem[] = itemsArray.map(tv => ({
        label: tv.label,
        kind: monaco.languages.CompletionItemKind.Variable,
        insertText: alreadyHasClosing ? ` ${tv.value} }}$0` : ` ${tv.value} }}$0`,
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range: replaceRange,
        sortText: `0_${tv.value}`,
        filterText: tv.value,
        detail: tv.detail,
        documentation: {
          value: `**${tv.label}**\n\n${tv.value ?? ''}`,
          isTrusted: true,
        },
      }));

      // Always return suggestions as incomplete when inside braces to keep the widget open
      return {
        suggestions,
        incomplete: true,
      };
    },
  };

  monaco.languages.registerCompletionItemProvider('template', provider);
  templateProviderRegistered = true;
};
